import React, { useState } from 'react';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon, 
  ClockIcon, 
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    inquiryType: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const inquiryTypes = [
    'General Inquiry',
    'Medicine Availability',
    'Prescription Refill',
    'Emergency Assistance'
  ];

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleInquiryTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, inquiryType: type }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ 
        name: '', 
        phone: '', 
        email: '', 
        inquiryType: '', 
        subject: '', 
        message: '' 
      });
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1200);
  };

  return (
    <div className="relative py-16 sm:py-24 bg-gradient-to-b from-slate-50 via-white to-emerald-50/25 overflow-hidden">
      
      {/* Ambient background glows */}
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-200/25 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
            Contact Us
          </h1>
        </div>

        {/* 2-Column Layout: Contact Cards (Left) & Form (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          
          {/* Left Column: Contact Cards */}
          <div className="lg:col-span-5 space-y-4 sm:space-y-5">
            
            {/* Phone & WhatsApp Card */}
            <div className="group bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 border border-slate-200/80 shadow-md shadow-slate-200/50 hover:shadow-xl hover:border-emerald-300 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md shadow-emerald-500/20 flex-shrink-0 group-hover:scale-105 transition-transform">
                  <PhoneIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-1">
                    Call & WhatsApp
                  </h3>
                  <a 
                    href="tel:+919673058787" 
                    className="text-lg sm:text-xl font-bold text-slate-900 hover:text-emerald-600 transition-colors block"
                  >
                    +91 96730 58787
                  </a>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    Instant response for emergency medicine queries & home refills.
                  </p>
                  
                  <div className="flex gap-2 mt-3.5">
                    <a 
                      href="tel:+919673058787"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-colors"
                    >
                      <PhoneIcon className="w-3.5 h-3.5" />
                      Direct Call
                    </a>
                    <a 
                      href="https://wa.me/919673058787" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors shadow-sm"
                    >
                      <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                      WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Card */}
            <div className="group bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 border border-slate-200/80 shadow-md shadow-slate-200/50 hover:shadow-xl hover:border-teal-300 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-md shadow-teal-500/20 flex-shrink-0 group-hover:scale-105 transition-transform">
                  <EnvelopeIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-teal-600 mb-1">
                    Email Support
                  </h3>
                  <a 
                    href="mailto:saipharmacy3132@gmail.com" 
                    className="text-base sm:text-lg font-bold text-slate-900 hover:text-teal-600 transition-colors break-all"
                  >
                    saipharmacy3132@gmail.com
                  </a>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    Send inquiries, doctor prescriptions, or billing requests.
                  </p>
                </div>
              </div>
            </div>

            {/* Store Location Card */}
            <div className="group bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 border border-slate-200/80 shadow-md shadow-slate-200/50 hover:shadow-xl hover:border-emerald-300 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20 flex-shrink-0 group-hover:scale-105 transition-transform">
                  <MapPinIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-1">
                    Store Location
                  </h3>
                  <div className="text-base font-bold text-slate-900">
                    Sai Pharmacy
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 mt-0.5 leading-relaxed">
                    Main Road, Kasaba Tarale, Maharashtra - 416211
                  </p>
                  <a 
                    href="https://www.google.com/search?sca_esv=0fbaab20bcead59f&sxsrf=APpeQnvuzSke2LNAIo0cIgt0LvOygWxxGg:1786339394975&q=sai+pharmacy+kasaba+tarale+address&ludocid=8473301955907776150&sa=X&ved=2ahUKEwiHvNfoqJWWAxUHjOEIHYwpJucQ6BN6BAg5EAI" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 hover:underline mt-2.5"
                  >
                    <span>View on Google Maps</span>
                    <span>→</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Working Hours Card */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl p-6 text-white shadow-xl shadow-slate-900/10 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 flex-shrink-0">
                  <ClockIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                      Operating Hours
                    </h3>
                    <span className="text-[10px] uppercase font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                      Open 7 Days
                    </span>
                  </div>
                  <div className="text-base font-bold text-white mt-1">
                    8:00 AM – 10:00 PM
                  </div>
                  <p className="text-xs text-slate-300 mt-1">
                    Emergency counter and on-call medicine dispatch available 24/7.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Premium Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white/90 backdrop-blur-xl p-6 sm:p-8 md:p-10 rounded-3xl sm:rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-200/80">
              
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  Send a Direct Message
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">
                  Fill in your details below and our pharmacy team will reach back shortly.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* 1. Inquiry Type Chips */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    What can we help you with?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {inquiryTypes.map((type) => {
                      const isSelected = formData.inquiryType === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleInquiryTypeSelect(type)}
                          className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 border ${
                            isSelected
                              ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-500/20'
                              : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                          }`}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 2. Name & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-slate-700 mb-1">
                      Your Full Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="name" 
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="block w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all duration-200" 
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="tel" 
                      id="phone" 
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="block w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all duration-200" 
                    />
                  </div>
                </div>

                {/* 3. Message */}
                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-slate-700 mb-1">
                    Your Message / Prescription Requirement <span className="text-red-500">*</span>
                  </label>
                  <textarea 
                    id="message" 
                    rows="3" 
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="block w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all duration-200 resize-none min-h-[90px]" 
                  ></textarea>
                </div>

                {/* 4. Privacy Badge */}
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-200/60">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span>Your medical details & contact numbers are kept strictly confidential.</span>
                </div>

                {/* 5. Submit Button */}
                <div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className={`w-full py-3 px-6 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-sm sm:text-base font-bold rounded-xl shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/35 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Sending Message...</span>
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                  
                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm flex items-center gap-2.5 animate-fadeIn">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div>
                        <span className="font-bold">Message sent successfully!</span> Our team at Sai Pharmacy will contact you shortly.
                      </div>
                    </div>
                  )}
                </div>

              </form>
            </div>
          </div>

        </div>

      </div>

      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}} />
    </div>
  );
};

export default Contact;

